
import { supabase } from "../integrations/supabase/client";

interface NotificationPayload {
  recipient_user_id: string;
  sender_user_id?: string;
  content: string;
  type: 'like' | 'comment' | 'follow' | 'system' | 'reminder';
  related_entity_id?: string;
  related_entity_type?: 'post' | 'comment' | 'user' | 'pet' | 'medical';
  send_push?: boolean;
}

export const triggerNotification = async (payload: NotificationPayload) => {
  try {
    // First create the notification
    const { data: notificationData, error: notificationError } = await supabase.functions.invoke('send-notification', {
      body: payload,
    });

    if (notificationError) {
      console.error("Error sending notification:", notificationError);
      return { success: false, error: notificationError };
    }

    // If send_push is true, send a push notification
    if (payload.send_push !== false) {
      const { data: pushData, error: pushError } = await supabase.functions.invoke('send-push', {
        body: {
          user_id: payload.recipient_user_id,
          title: getNotificationTitle(payload.type),
          body: payload.content,
          data: {
            type: payload.type,
            related_entity_id: payload.related_entity_id,
            related_entity_type: payload.related_entity_type,
            url: getNotificationUrl(payload.type, payload.related_entity_id, payload.related_entity_type),
          }
        }
      });

      if (pushError) {
        console.error("Error sending push notification:", pushError);
        // Continue anyway since the notification was created
      }
    }

    return { success: true, data: notificationData };
  } catch (err) {
    console.error("Exception sending notification:", err);
    return { success: false, error: err };
  }
};

// Get a user-friendly title based on notification type
function getNotificationTitle(type: string): string {
  switch (type) {
    case 'like':
      return 'New Like';
    case 'comment':
      return 'New Comment';
    case 'follow':
      return 'New Follower';
    case 'system':
      return 'System Notification';
    case 'reminder':
      return 'Medical Reminder';
    default:
      return 'Petius Notification';
  }
}

// Get the URL to direct users to when they click the notification
function getNotificationUrl(type: string, entityId?: string, entityType?: string): string {
  switch (type) {
    case 'like':
    case 'comment':
      return entityId ? `/post/${entityId}` : '/feed';
    case 'follow':
      return entityId ? `/profile/${entityId}` : '/profile';
    case 'reminder':
      return entityType === 'pet' && entityId ? 
        `/medical/${entityId}` : '/notifications';
    default:
      return '/notifications';
  }
}

export const sendLikeNotification = async (recipientId: string, senderId: string, postId: string) => {
  try {
    // Fetch sender and pet information for personalized message
    const { data: sender } = await supabase
      .from('users')
      .select('full_name')
      .eq('id', senderId)
      .single();

    const { data: post } = await supabase
      .from('memory_posts')
      .select(`
        pets!inner(name)
      `)
      .eq('id', postId)
      .single();

    const senderName = sender?.full_name || 'Someone';
    const petName = post?.pets?.name || 'your pet';
    const content = `${senderName} liked ${petName}'s post`;

    return triggerNotification({
      recipient_user_id: recipientId,
      sender_user_id: senderId,
      content,
      type: 'like',
      related_entity_id: postId,
      related_entity_type: 'post'
    });
  } catch (error) {
    console.error('Error creating personalized like notification:', error);
    // Fallback to generic message
    return triggerNotification({
      recipient_user_id: recipientId,
      sender_user_id: senderId,
      content: "Someone liked your post",
      type: 'like',
      related_entity_id: postId,
      related_entity_type: 'post'
    });
  }
};

export const sendCommentNotification = async (recipientId: string, senderId: string, postId: string) => {
  try {
    // Fetch sender and pet information for personalized message
    const { data: sender } = await supabase
      .from('users')
      .select('full_name')
      .eq('id', senderId)
      .single();

    const { data: post } = await supabase
      .from('memory_posts')
      .select(`
        pets!inner(name)
      `)
      .eq('id', postId)
      .single();

    const senderName = sender?.full_name || 'Someone';
    const petName = post?.pets?.name || 'your pet';
    const content = `${senderName} commented on ${petName}'s post`;

    return triggerNotification({
      recipient_user_id: recipientId,
      sender_user_id: senderId,
      content,
      type: 'comment',
      related_entity_id: postId,
      related_entity_type: 'post'
    });
  } catch (error) {
    console.error('Error creating personalized comment notification:', error);
    // Fallback to generic message
    return triggerNotification({
      recipient_user_id: recipientId,
      sender_user_id: senderId,
      content: "Someone commented on your post",
      type: 'comment',
      related_entity_id: postId,
      related_entity_type: 'post'
    });
  }
};

export const sendFollowNotification = async (recipientId: string, senderId: string) => {
  return triggerNotification({
    recipient_user_id: recipientId,
    sender_user_id: senderId,
    content: "Someone started following you",
    type: 'follow',
    related_entity_id: senderId,
    related_entity_type: 'user'
  });
};

export const sendMedicalReminderNotification = async (recipientId: string, petId: string, message: string) => {
  return triggerNotification({
    recipient_user_id: recipientId,
    content: message,
    type: 'reminder',
    related_entity_id: petId,
    related_entity_type: 'pet'
  });
};
